/**
 * Investment calculators — pure finance math.
 *
 * All functions return `null` for non-finite/invalid inputs so the UI can show a
 * hint instead of NaN. Money values are returned at full precision; the UI
 * rounds for display.
 */

function finite(...nums: number[]): boolean {
  return nums.every((n) => typeof n === "number" && Number.isFinite(n));
}

export interface InvestmentResult {
  /** Maturity / future value. */
  futureValue: number;
  /** Total amount invested (principal contributions). */
  invested: number;
  /** Gains = futureValue − invested. */
  returns: number;
}

/**
 * Monthly SIP future value (contribution at the start of each month — an
 * annuity due): FV = P · [((1+i)^n − 1) / i] · (1+i), i = monthly rate.
 */
export function sip(
  monthly: number,
  annualRatePct: number,
  years: number,
): InvestmentResult | null {
  if (!finite(monthly, annualRatePct, years) || monthly < 0 || years <= 0) {
    return null;
  }
  const n = Math.round(years * 12);
  const i = annualRatePct / 100 / 12;
  const futureValue =
    i === 0 ? monthly * n : monthly * (((1 + i) ** n - 1) / i) * (1 + i);
  const invested = monthly * n;
  return { futureValue, invested, returns: futureValue - invested };
}

/**
 * Lump-sum future value with compounding `compoundsPerYear` times a year:
 * FV = P · (1 + r/m)^(m·t).
 */
export function lumpSum(
  principal: number,
  annualRatePct: number,
  years: number,
  compoundsPerYear = 1,
): InvestmentResult | null {
  if (
    !finite(principal, annualRatePct, years, compoundsPerYear) ||
    principal < 0 ||
    years < 0 ||
    compoundsPerYear < 1
  ) {
    return null;
  }
  const r = annualRatePct / 100;
  const futureValue =
    principal * (1 + r / compoundsPerYear) ** (compoundsPerYear * years);
  return { futureValue, invested: principal, returns: futureValue - principal };
}

export interface CompoundResult {
  amount: number;
  interest: number;
  principal: number;
}

/** Compound interest: final amount and interest earned. */
export function compoundInterest(
  principal: number,
  annualRatePct: number,
  years: number,
  compoundsPerYear = 1,
): CompoundResult | null {
  const r = lumpSum(principal, annualRatePct, years, compoundsPerYear);
  if (!r) return null;
  return { amount: r.futureValue, interest: r.returns, principal };
}

/** Round to whole units (money display). */
export function roundMoney(value: number): number {
  return Math.round(value);
}
